import { useEffect, useState } from "react";
import API from "../../services";
import Loader from "../../components/Loader";
import ErrorMessage from "../../components/ErrorMessage";
import { Colors } from "../../utils/colors";
import { Fonts } from "../../utils/fonts";
import PublicationsShelf from "./PublicationsShelf";
import { FontSizes } from "../../assets/fonts/fontSizes";
import { MEDIA_QUERIES } from "../../constants/breakpoints";
import { LAYOUT } from "../../constants/layout";
import { Z_INDEX } from "../../constants/zIndex";
import { useSidebarWidth } from "../../hooks/useResponsive";

type Category = {
  publicationCategoryGuid: string;
  publicationCategoryName: string;
  publicationCategoryId:number;
};

export default function DocumentsScreen() {
  const [categories, setCategories] = useState<Category[]>([]);
  const [selectedIndex, setSelectedIndex] = useState(() => {
  const saved = sessionStorage.getItem("publications_selectedIndex");
  return saved ? parseInt(saved, 10) : 0;
});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const sidebarWidth = useSidebarWidth();

  useEffect(() => {
    (async () => {
      try {
        const data = (await API.getPublicationsCategories()) as Category[];
        setCategories(data ?? []);
        setLoading(false);
        setError(false);
      } catch {
        setError(true);
        setLoading(false);
      }
    })();
  }, []);

  if (loading) return <Loader />;
  if (error) return <ErrorMessage />;
  if (!categories.length) return <ErrorMessage />;

  const selected = categories[Math.min(selectedIndex, categories.length - 1)];

  const handleCategorySelect = (index: number) => {
    setSelectedIndex(index);
    sessionStorage.setItem("publications_selectedIndex", String(index));
    setIsDrawerOpen(false); // Close drawer on mobile after selection
  };

  console.log(selected,'selected section');
  

  return (
    <>
      <style>{`
        ${MEDIA_QUERIES.aboveTablet} {
          .drawer-menu-button {
            display: none !important;
          }
          .drawer-sidebar {
            transform: translateX(0) !important;
            top: ${LAYOUT.topbarHeight}px !important;
            height: calc(100vh - ${LAYOUT.topbarHeight}px) !important;
            padding-top: 0 !important;
            background: transparent !important;
          }
          .drawer-close-button {
            display: none !important;
          }
          .drawer-category-title {
            padding-top: 8px !important;
          }
        }
        ${MEDIA_QUERIES.tablet} {
          .drawer-overlay {
            display: block !important;
          }
          .drawer-content {
            margin-left: 0 !important;
            padding-left: 60px !important;
            padding-right: 20px !important;
            padding-top: 60px !important;
          }
          .drawer-close-button {
            display: flex !important;
          }
        }
        ${MEDIA_QUERIES.aboveLarge} {
          .drawer-category-title {
            font-size: ${FontSizes.md} !important;
            padding-left: 24px !important;
          }
          .drawer-category-text {
            font-size: ${FontSizes.sm} !important;
          }
          .drawer-category-item {
            max-height: 48px !important;
            padding-left: 24px !important;
          }
        }
        ${MEDIA_QUERIES.aboveWide} {
          .drawer-category-title {
            font-size: ${FontSizes.lg} !important;
            padding-left: 28px !important;
          }
          .drawer-category-text {
            font-size: ${FontSizes.md} !important;
          }
          .drawer-category-item {
            max-height: 54px !important;
            padding-left: 28px !important;
          }
        }
      `}</style>

      <div style={styles.container}>
        {/* MENU BUTTON - Mobile Only */}
        {!isDrawerOpen && (
          <button
            type="button"
            onClick={() => setIsDrawerOpen(true)}
            style={styles.menuButton}
            className="drawer-menu-button"
            aria-label="Open menu"
          >
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
              <path
                d="M3 12H21M3 6H21M3 18H21"
                stroke="white"
                strokeWidth="2"
                strokeLinecap="round"
              />
            </svg>
          </button>
        )}

        {/* OVERLAY - Mobile Only */}
        {isDrawerOpen && (
          <div
            style={styles.overlay}
            className="drawer-overlay"
            onClick={() => setIsDrawerOpen(false)}
          />
        )}

        {/* LEFT SIDEBAR / DRAWER */}
        <div
          className="drawer-sidebar"
          style={{
            ...styles.sidebar,
            minWidth: sidebarWidth,
            width: sidebarWidth,
            transform: isDrawerOpen ? "translateX(0)" : "translateX(-100%)",
            background: isDrawerOpen ? Colors.bgDrawerMobile : "transparent",
          }}
        >
          {/* CLOSE BUTTON - Mobile Only */}
          <button
            type="button"
            onClick={() => setIsDrawerOpen(false)}
            style={styles.closeButton}
            className="drawer-close-button"
            aria-label="Close menu"
          >
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
              <path
                d="M18 6L6 18M6 6L18 18"
                stroke="white"
                strokeWidth="2"
                strokeLinecap="round"
              />
            </svg>
          </button>

          <div style={styles.categoryTitle} className="drawer-category-title">
            Choose Category
          </div>

          {categories?.map((item, index) => {
            const active = selectedIndex === index;
            
            const background = active
              ? Colors.activeBackground
              : Colors.bgCategoryDefault;

            return (
              <button
                key={item.publicationCategoryGuid}
                type="button"
                onClick={() => handleCategorySelect(index)}
                style={{
                  ...styles.categoryItem,
                  background,
                  borderColor: active ? Colors.SecondaryColor : Colors.borderDefault,
                }}
              >
                <span
                  style={{
                    ...styles.categoryText,
                    ...(active ? styles.categoryTextActive : {}),
                  }}
                >
                  {item.publicationCategoryName}
                </span>
              </button>
            );
          })}
        </div>

        {/* RIGHT CONTENT */}
        <div
          style={{ ...styles.content, marginLeft: sidebarWidth }}
          className="drawer-content"
        >
          <PublicationsShelf
            categoryGuid={String(selected.publicationCategoryGuid)}
            titleOverride={selected.publicationCategoryName}
          />
        </div>
      </div>
    </>
  );
}

const styles: Record<string, React.CSSProperties> = {
  container: {
    display: "flex",
    flexDirection: "row",
    minHeight: `calc(100vh - ${LAYOUT.topbarHeight}px)`,
    position: "relative",
  },
  menuButton: {
    position: "fixed",
    top: LAYOUT.topbarHeight + 10,
    left: 10,
    zIndex: Z_INDEX.drawerButton,
    background: Colors.bgMenuButton,
    border: `1px solid ${Colors.borderMenuButton}`,
    borderRadius: 8,
    padding: 10,
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    backdropFilter: "blur(10px)",
  },
  overlay: {
    position: "fixed",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: Colors.overlayDark,
    backdropFilter: "blur(8px)",
    zIndex: Z_INDEX.drawerOverlay,
    display: "none",
  },
  sidebar: {
    position: "fixed",
    top: 0,
    left: 0,
    height: "100vh",
    overflowY: "auto",
    paddingBottom: 8,
    paddingTop: LAYOUT.topbarHeight,
    zIndex: Z_INDEX.drawer,
    background: "transparent",
    transition: "transform 0.3s ease-in-out",
    backdropFilter: "blur(10px)",
  },
  closeButton: {
    position: "absolute",
    top: 16,
    right: 16,
    zIndex: Z_INDEX.drawerButton,
    background: Colors.bgCloseButton,
    border: `1px solid ${Colors.borderActive}`,
    borderRadius: 8,
    padding: 8,
    cursor: "pointer",
    display: "none",
    alignItems: "center",
    justifyContent: "center",
    backdropFilter: "blur(10px)",
  },
  categoryTitle: {
    fontSize: FontSizes.sm,
    color: Colors.White,
    fontWeight: Fonts.Medium,
    fontFamily: "Inter",
    paddingBottom: 12,
    paddingLeft: 20,
  },
  categoryItem: {
    width: "100%",
    maxHeight: 42,
    padding: "10px",
    margin: "3px 0",
    paddingLeft: 20,
    paddingRight: 10,
    borderTopWidth: 1,
    borderBottomWidth: 1,
    borderRightWidth: 1,
    borderLeftWidth: 0,
    borderStyle: "solid",
    borderTopRightRadius: 30,
    borderBottomRightRadius: 30,
    display: "flex",
    cursor: "pointer",
    background: "transparent",
    outline: "none",
  },
  categoryText: {
    fontSize: FontSizes.xs,
    color: Colors.textGray,
    fontWeight: Fonts.Medium,
    fontFamily: "Inter",
  },
  categoryTextActive: {
    fontFamily: "Inter",
    color: Colors.White,
    fontWeight: Fonts.Bold,
  },
  content: {
    flex: 1,
    paddingLeft: 40,
    paddingRight: 40,
  },
};
